/**
 * BRANCH (OUTLET) SELECTION — client side.
 *
 * ============================= THIS IS AN ADDITION ===========================
 * Nothing in the recovered storefront corresponds to this file. The original
 * resolves ONE tenant from the hostname and ships one deployment per branch, so
 * it has no notion of "the branch you are on" being changeable. See the header
 * of `src/lib/branch.ts` for what is recovered and what is not.
 * =============================================================================
 *
 * The server has already decided the branch by the time this mounts — it is on
 * `pageProps.branch`, and the served HTML is that branch's. This provider does
 * three things on top of that:
 *
 *   1. Tells `src/lib/config.ts` which branch the fetchers should default to.
 *   2. Guards the cart against a branch it was not priced for.
 *   3. Owns the picker's open state and performs the switch.
 */
import { useRouter } from "next/router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ROUTE_HOME, ROUTE_MENU } from "@/constants/routes";
import { setActiveTenantId } from "@/lib/config";
import { writeBranchCookie } from "@/lib/branch";
import { useAppDispatch } from "@/store";
import { clearCart } from "@/store/slices/cartSlice";
import { setInitiateDineIn } from "@/store/slices/dineinSlice";
import { persistor } from "@/store";
import type { Branch, BranchContextValue } from "@/types";

/**
 * The branch the PERSISTED state belongs to.
 *
 * Kept beside redux-persist's own key rather than inside it, so this addition
 * does not change the shape of any recovered slice. It is the second half of
 * the cart guard: the cookie says which branch to SERVE, this says which branch
 * the basket in localStorage was filled at, and a disagreement means the basket
 * is stale.
 */
const PERSISTED_BRANCH_KEY = "eposmatic-web:branch";

interface BranchApi extends BranchContextValue {
  current: Branch | null;
  pickerOpen: boolean;
  openPicker: () => void;
  closePicker: () => void;
  /** Commit a branch. Same branch => just closes. */
  chooseBranch: (tenantId: string) => void;
  switching: boolean;
}

const FALLBACK: BranchContextValue = {
  tenantId: "",
  source: "default",
  brandName: "",
  branches: [],
  needsChoice: false,
};

const BranchContext = createContext<BranchApi | null>(null);

export const useBranch = (): BranchApi => {
  const value = useContext(BranchContext);
  if (!value) {
    // A page rendered outside the provider still has to work: the storefront
    // has surfaces (the 404 and error pages) that mount no provider tree.
    return {
      ...FALLBACK,
      current: null,
      pickerOpen: false,
      openPicker: () => {},
      closePicker: () => {},
      chooseBranch: () => {},
      switching: false,
    };
  }
  return value;
};

export function BranchProvider({
  branch,
  children,
}: {
  branch?: BranchContextValue;
  children: ReactNode;
}) {
  const resolved = branch ?? FALLBACK;
  const { tenantId, branches, needsChoice } = resolved;
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const guarded = useRef(false);

  /**
   * Point the fetcher defaults at this branch BEFORE any child renders.
   *
   * Deliberately during render, not in an effect: `src/lib/api.ts` reads
   * `activeTenantId()` at call time, and a child's mount effect can fire a
   * fetch before this component's own effects run. The setter is a no-op on the
   * server (see the note in `config.ts`), so this cannot leak across requests.
   */
  if (tenantId) setActiveTenantId(tenantId);

  /**
   * THE CART GUARD.
   *
   * A basket carried across a branch switch is a correctness bug, not a
   * convenience: prices, availability, modifier options, delivery areas and
   * tables are all per-branch, and an item that is Rs 950 at F8 may not exist at
   * DHA. Keeping those lines and re-pricing them silently would be worse than
   * losing them, so the basket is CLEARED.
   *
   * This runs on mount rather than only inside `chooseBranch` because the branch
   * can change without going through the picker at all — a `?branch=` deep
   * link, a per-branch domain, a cookie edited or expired, or the same browser
   * arriving on a different host. Comparing what the basket was filled at with
   * what is being served catches every one of those.
   *
   * The dine-in floor plan is reset with it: it is persisted (a hard reload of
   * the checkout must still be able to name the chosen table) and it is a
   * different restaurant's furniture.
   *
   * The signed-in customer is NOT cleared. Identity is a brand-level thing — the
   * same phone number, the same wallet — and forcing a re-login on a branch
   * switch would be gratuitous.
   */
  useEffect(() => {
    if (!tenantId || guarded.current) return;
    guarded.current = true;
    let previous: string | null = null;
    try {
      previous = window.localStorage.getItem(PERSISTED_BRANCH_KEY);
    } catch {
      previous = null;
    }
    if (previous && previous !== tenantId) {
      // Android WebView can restore its original source URL without restoring
      // the branch cookie. On a shared brand domain that SSR request therefore
      // falls back to the build's Commercial tenant even though this device
      // explicitly selected PWD. Recover only when the server had NO explicit
      // selector (default source) and the saved tenant still belongs to this
      // brand. Host/query/cookie decisions remain authoritative.
      if (
        resolved.source === "default" &&
        branches.some((entry) => entry._id === previous)
      ) {
        const target = new URL(window.location.href);
        target.searchParams.set("branch", previous);
        window.location.replace(`${target.pathname}${target.search}${target.hash}`);
        return;
      }
      dispatch(clearCart());
      dispatch(setInitiateDineIn([]));
      void persistor.flush();
    }
    try {
      window.localStorage.setItem(PERSISTED_BRANCH_KEY, tenantId);
    } catch {
      /* private mode — the guard degrades to "never clears", never to a wrong clear */
    }
  }, [branches, dispatch, resolved.source, tenantId]);

  /**
   * First-run gate, and ONLY on the landing page.
   *
   * `needsChoice` is the server's verdict: nothing chose this branch — no
   * per-branch host, no cookie, no query — and there is more than one to choose
   * between. That is precisely the situation on this deployment, which is
   * reached by bare IP. A deployment on a real per-branch domain never sees it.
   *
   * Restricted to `/` so a deep link into a menu, a table QR code or an order
   * receipt is never interrupted by a dialog; those surfaces reach the picker
   * through the branch chip instead.
   */
  useEffect(() => {
    if (needsChoice && router.pathname === ROUTE_HOME) setPickerOpen(true);
  }, [needsChoice, router.pathname]);

  const current = useMemo(
    () => branches.find((entry) => entry._id === tenantId) ?? null,
    [branches, tenantId],
  );

  const chooseBranch = useCallback(
    (next: string) => {
      if (!next) return;
      if (next === tenantId) {
        // Confirming the branch you are already on is still a CHOICE: persist it
        // so the gate does not reopen on the next visit.
        writeBranchCookie(next);
        setPickerOpen(false);
        return;
      }
      setSwitching(true);
      writeBranchCookie(next);
      dispatch(clearCart());
      dispatch(setInitiateDineIn([]));
      try {
        window.localStorage.setItem(PERSISTED_BRANCH_KEY, next);
      } catch {
        /* see above */
      }
      // Give redux-persist a brief chance to write the cleared basket, but do
      // not make navigation depend on it. In real Chrome `flush()` can remain
      // pending indefinitely (storage lock/eviction), which left the picker on
      // screen after the user pressed Continue and made branch switching look
      // completely broken. The separate branch marker still makes the next
      // mount clear any stale rehydrated basket, so a bounded wait is safe.
      const flushOrTimeout = Promise.race([
        persistor.flush().catch(() => undefined),
        new Promise<void>((resolve) => window.setTimeout(resolve, 250)),
      ]);
      void flushOrTimeout.finally(() => {
        // A FULL load, not a client-side route change: the whole point is to
        // re-run `getServerSideProps` so settings, menu, delivery areas, tables
        // and the live feed are all re-derived for the new branch on the server.
        // `?branch=` is carried so the very first render is correct even if the
        // cookie has not been written yet.
        // Keep the operator on the equivalent menu surface. Sending every
        // switch back to `/` made the branch picker look broken in the staff
        // app and discarded the selected order type. Private/order routes are
        // intentionally reduced to the menu because their ids belong to the
        // previous branch.
        const currentPath = router.asPath.split("#", 1)[0] || ROUTE_HOME;
        const safePath =
          currentPath === ROUTE_HOME ||
          currentPath.startsWith("/menu") ||
          currentPath.startsWith("/digital-menu")
            ? currentPath
            : ROUTE_MENU;
        const target = new URL(safePath, window.location.origin);
        target.searchParams.set("branch", next);
        window.location.assign(`${target.pathname}${target.search}${target.hash}`);
      });
    },
    [dispatch, router.asPath, tenantId],
  );

  const value = useMemo<BranchApi>(
    () => ({
      ...resolved,
      current,
      pickerOpen,
      openPicker: () => setPickerOpen(true),
      closePicker: () => {
        // Dismissing the first-run gate counts as accepting the branch on
        // screen, which is the one already rendered — so record it rather than
        // asking again on the next page.
        if (needsChoice && tenantId) writeBranchCookie(tenantId);
        setPickerOpen(false);
      },
      chooseBranch,
      switching,
    }),
    [resolved, current, pickerOpen, chooseBranch, switching, needsChoice, tenantId],
  );

  return (
    <BranchContext.Provider value={value}>{children}</BranchContext.Provider>
  );
}
